import * as React from "react";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { connect } from "react-redux";
import { Container, Button } from "semantic-ui-react";
import { uniq, flatten, values, debounce } from "lodash";

import { openSection, submitInvalidQuestionnaire, clearQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import {
    QUESTIONNAIRE_KEY as BBV_CONSULTATION_QUESTIONNAIRE_KEY,
    BbvConsultationQuestionnaire
} from "@teamprevent/common/bbvConsultation/components/bbvConsultationQuestionnaire";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { SavingIndicator } from "@teamprevent/common/components/SavingIndicator";
import { TaskModel, TaskTypes } from "@teamprevent/common/task/taskActions/models";

import { toast } from "@teamprevent/common/toasts";

import { ApplicationState } from "../../applicationState";
import { PartialOhCaseListModel } from "../../crud/ohCase";
import { routeIdSelector as ohCaseIdSelector } from "../../crud/ohCase/selectors";
import { submitBbvConsultation, loadBbvConsultation, saveBbvConsultation } from "../actions";
import { AddTaskFormModel, TaskType, AddTaskModel, ExcludeBbvTaskTypes, mapToCreateTaskModel } from "../../task/model";
import { TasksApi } from "../../tasks/tasksApi";

import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { loadBbvReportSuccessorTasks } from "../../bbvReport/actions";
import { AddNextTaskFormModel } from "../../tasks/models";

interface BbvReportStateProps {
    readonly: boolean;
    customerId?: string;
    taskId?: string;
    task?: TaskModel;
    ohCaseId?: string;
    ohCase?: PartialOhCaseListModel;
    successorTasks: TaskType[];
    questions: AnsweredQuestionWithId[];
    eTag?: string;
}

interface BbvReportDispatchProps {
    startAssessment: () => void;
    loadQuestionnaire: (ohCaseId: string, taskId: string, customerId: string) => void;
    navigateBack: () => void;
    submitInvalidQuestionnaire: () => void;
    submitQuestionnaire: (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: AddTaskModel[]) => void;
    saveQuestionnaire?: (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => void;
    loadSuccessorTasks: () => void;
    clearQuestionnaire: () => void;
}

interface BbvReportState {
    submitted: boolean;
    tasks: { task: AddTaskFormModel; isValid: boolean }[];
    saving: boolean;
}

class BbvConsultationUnconnected extends React.Component<BbvReportDispatchProps & BbvReportStateProps, BbvReportState> {
    private crBugs11_5_ToggleEnabled = false;

    constructor(props: BbvReportDispatchProps & BbvReportStateProps) {
        super(props);
        this.state = { submitted: false, tasks: [], saving: false };
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
        this.crBugs11_5_ToggleEnabled = getFeatureToggles()[FeatureToggleNames.CrBugs11_5];
    }

    private saveDebounced = debounce(async () => {
        try {
            if (!this.props.saveQuestionnaire || this.state.submitted) {
                return;
            }
            this.setState({ saving: true });
            await this.props.saveQuestionnaire(this.props.ohCaseId, this.props.taskId, this.props.questions, this.props.eTag);
            this.setState({ saving: false });
        } catch (ex: any) {
            if (ex.status === 400) {
                this.loadQuestionnaire();
            } else {
                throw ex;
            }
        } finally {
            this.setState({ saving: false });
        }
    }, 1500);

    public UNSAFE_componentWillMount() {
        this.props.clearQuestionnaire();
    }

    public componentDidMount() {
        if (!this.props.successorTasks || !this.props.successorTasks.length) {
            this.props.loadSuccessorTasks();
        }
    }

    public componentDidUpdate(prevProps: Readonly<BbvReportStateProps>) {
        if (!this.props.readonly && !this.state.submitted &&
            this.props.saveQuestionnaire && this.props.questions.some((q, i) => q !== prevProps.questions[i])) {
            this.saveDebounced();
        }
    }

    public async submitQuestionnaire(questions: AnsweredQuestionWithId[]): Promise<boolean> {
        if (this.crBugs11_5_ToggleEnabled && this.state.submitted) {
            toast.error("The form has already been submitted");
            return false;
        }

        this.setState({ submitted: true });

        if (!questions.every(question => question.isValid)) {
            this.props.submitInvalidQuestionnaire();
            if (this.crBugs11_5_ToggleEnabled) {
                this.setState({ submitted: false });
            }
            return false;
        }

        if (this.state.tasks.some(t => !t.isValid)) {
            if (this.crBugs11_5_ToggleEnabled) {
                this.setState({ submitted: false });
            }
            return false;
        }

        const createTasks = this.state.tasks.map(t => mapToCreateTaskModel(t.task, this.props.ohCaseId));

        await this.props.submitQuestionnaire(this.props.ohCaseId, this.props.taskId, questions, createTasks);

        this.props.navigateBack();

        return true;
    }

    public render() {
        const successorTasks = this.props.successorTasks.filter(t => !ExcludeBbvTaskTypes.includes(TaskTypes[t.typeId]));
        const extraSections = !this.props.readonly &&
            this.props.successorTasks ? [{
                id: "NextTask",
                title: "Next Task",
                content: (
                    <AddTasksInputs
                        taskTypes={successorTasks}
                        onChange={this.updateTasks}
                        ohCaseId={this.props.ohCaseId}
                        showErrors={this.state.submitted}
                        assignmentOptionRequired={true}
                    />)
            }
            ] : [];
        return (
            <Container className="bbv-report">
                <h2>Blood borne virus assessment for {this.props.ohCase && `${this.props.ohCase.employeeFirstName} ${this.props.ohCase.employeeLastName}`}</h2>
                {this.props.readonly ? null :
                    <>
                        <Container textAlign="right" className="start-button">
                            <DidNotAttendButton
                                customerId={this.props.customerId}
                                ohCaseId={this.props.ohCaseId}
                                task={this.props.task}
                            />
                            <Button onClick={this.props.startAssessment} content="Start Assessment" />
                        </Container>
                    </>}
                <BbvConsultationQuestionnaire
                    questions={this.props.questions}
                    extraSections={extraSections}
                    readonly={this.props.readonly}
                    employeeName={this.props.ohCase && `${this.props.ohCase.employeeFirstName} ${this.props.ohCase.employeeLastName}`}
                    startAssessment={this.props.startAssessment}
                    loadQuestionnaire={this.loadQuestionnaire}
                    submitQuestionnaire={this.submitQuestionnaire}
                    task={this.props.task}
                    removeAppointment={new TasksApi().removeAppointment}
                />
                <SavingIndicator display={this.state.saving} />
            </Container>
        );
    }

    private updateTasks = (tasks: AddNextTaskFormModel[]) => this.setState({ tasks });

    private loadQuestionnaire = () => this.props.loadQuestionnaire(this.props.ohCaseId, this.props.taskId, this.props.customerId);
}

const mapStateToProps = (state: ApplicationState): BbvReportStateProps => {
    const taskId = state.router.params && state.router.params.taskId;
    let task = state.actionableTasks.tasks.find(t => t.id === taskId);
    const ohCaseId = ohCaseIdSelector(state) || (task && task.caseId);
    const ohCase = state.ohCases.find(c => c.id === ohCaseId);
    if (ohCase && ohCase.tasks) {
        task = task || ohCase.tasks.find(t => t.id === taskId);
    }
    return {
        questions: state.questionnaires[BBV_CONSULTATION_QUESTIONNAIRE_KEY] ?
            uniq(flatten(state.questionnaires[BBV_CONSULTATION_QUESTIONNAIRE_KEY].sections.map(section => values(section.questions)))) :
            [],
        eTag: state.questionnaires[BBV_CONSULTATION_QUESTIONNAIRE_KEY]?.eTag,
        customerId: (state.router.params && state.router.params.customerId) || (task && task.customer.id),
        ohCaseId,
        taskId,
        task,
        ohCase,
        readonly: task && task.completed,
        successorTasks: state.bbvReportSuccessorTasks
    };
};

const mapDispatchToProps = (dispatch: any): BbvReportDispatchProps => {
    return {
        startAssessment: () => dispatch(openSection({ sectionId: "PersonalDetails", questionnaireKey: BBV_CONSULTATION_QUESTIONNAIRE_KEY })),
        loadQuestionnaire: (ohCaseId: string, taskId: string) => dispatch(loadBbvConsultation(ohCaseId, taskId)),
        navigateBack: () => dispatch(navigateToParentRoute()),
        submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(BBV_CONSULTATION_QUESTIONNAIRE_KEY)),
        loadSuccessorTasks: () => dispatch(loadBbvReportSuccessorTasks()),
        submitQuestionnaire: (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: AddTaskModel[]) =>
            dispatch(submitBbvConsultation(ohCaseId, taskId, questions, tasks)),
        saveQuestionnaire: (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) =>
            dispatch(saveBbvConsultation(ohCaseId, taskId, questions, eTag)),
        clearQuestionnaire: () => dispatch(clearQuestionnaire(BBV_CONSULTATION_QUESTIONNAIRE_KEY))
    };
};

export const BbvConsultation = connect(mapStateToProps, mapDispatchToProps)(BbvConsultationUnconnected);
